import type { FeatureFlag } from '@/constants/featureFlags';

/**
 * Test-only overrides for feature flags.
 *
 * This is inert in production: nothing in the app ever calls the setter, and
 * `window.__PRIMUM_TEST__` is set only by a test harness before app code runs
 * (e.g. Playwright `addInitScript`). Real users never populate either source,
 * so `getFeatureFlagOverride` always returns `undefined` for them and the flag
 * hooks fall through to Flagsmith exactly as before.
 *
 * The purpose is to make the two axes a chat test must vary — feature flags and
 * (via the harness) persona — into deterministic knobs, instead of depending on
 * a live Flagsmith environment whose values can flip under the test.
 */

export type FeatureFlagOverrides = Partial<Record<FeatureFlag, boolean>>;

export type FeatureFlagValueOverrides = Partial<
  Record<FeatureFlag, string | boolean | Record<string, unknown>>
>;

/**
 * Config a Playwright test injects to switch chat onto the polling transport,
 * which reads messages from our backend REST API instead of Twilio. It carries
 * no seed messages — the transport fetches them — so the only knob is the poll
 * cadence. Crosses the `addInitScript` boundary, so it must stay
 * JSON-serializable. Its mere presence is the install signal
 * (see {@link hasInjectedChatTransport}); an empty object is valid.
 */
export type InjectedChatTransportConfig = {
  pollIntervalMs?: number;
};

type InjectedTestConfig = {
  featureFlags?: FeatureFlagOverrides;
  featureFlagValues?: FeatureFlagValueOverrides;
  chatTransport?: InjectedChatTransportConfig;
};

let flagOverrides: FeatureFlagOverrides | null = null;

let flagValueOverrides: FeatureFlagValueOverrides | null = null;

function readInjectedConfig(): InjectedTestConfig | null {
  if (typeof window === 'undefined') {
    return null;
  }

  return (window as unknown as { __PRIMUM_TEST__?: InjectedTestConfig }).__PRIMUM_TEST__ ?? null;
}

/** Set (or clear, with `null`) boolean flag overrides. Called only by tests. */
export function setFeatureFlagOverrides(next: FeatureFlagOverrides | null): void {
  flagOverrides = next;
}

/** Set (or clear, with `null`) multivariate flag-value overrides. Called only by tests. */
export function setFeatureFlagValueOverrides(next: FeatureFlagValueOverrides | null): void {
  flagValueOverrides = next;
}

export function getFeatureFlagOverride(flag: FeatureFlag): boolean | undefined {
  const active = flagOverrides ?? readInjectedConfig()?.featureFlags ?? null;

  return active ? active[flag] : undefined;
}

export function getFeatureFlagValueOverride(
  flag: FeatureFlag
): string | boolean | Record<string, unknown> | undefined {
  const active = flagValueOverrides ?? readInjectedConfig()?.featureFlagValues ?? null;

  return active ? active[flag] : undefined;
}

/**
 * Whether a test has injected a fake chat transport before the app booted.
 * Safe to call in production — real users never set `window.__PRIMUM_TEST__`,
 * so this is always `false` for them. Kept free of any fake-transport import so
 * production code can call it without bundling test code.
 */
export function hasInjectedChatTransport(): boolean {
  return !!readInjectedConfig()?.chatTransport;
}

export function getInjectedChatTransportConfig(): InjectedChatTransportConfig | undefined {
  return readInjectedConfig()?.chatTransport ?? undefined;
}
